import {CodeIcon, CopilotIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useAnalytics} from '@github-ui/use-analytics'

export type McpInstallTarget = {
  id?: string
  name: string
  full_name?: string
  api_name?: string
  url?: string
}

const getMcpRegistryHostPort = () => {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    return 'localhost:8080'
  }
  return 'api.mcp.github.com/2025-09-15'
}

export function getRegistryServerUrl(protocol: 'vscode' | 'vscode-insiders', mcp: McpInstallTarget): string
export function getRegistryServerUrl(protocol: string, mcp: McpInstallTarget & {id: string}): string
export function getRegistryServerUrl(protocol: string, mcp: McpInstallTarget): string | undefined
export function getRegistryServerUrl(protocol: string, mcp: McpInstallTarget) {
  const serverName = mcp.api_name || mcp.full_name || mcp.name
  if (protocol === 'vscode' || protocol === 'vscode-insiders') {
    return `${protocol}:mcp/by-name/${serverName}`
  }

  if (!mcp.id) return undefined
  return `${protocol}:mcp/${getMcpRegistryHostPort()}/v0/servers/${mcp.id}`
}

export function getCopilotAppInstallUrl(mcp: McpInstallTarget & {url: string}, entryPoint: string): string
export function getCopilotAppInstallUrl(mcp: McpInstallTarget, entryPoint: string): string | undefined
export function getCopilotAppInstallUrl(mcp: McpInstallTarget, entryPoint: string) {
  if (!mcp.url) return undefined

  const canonicalName = mcp.full_name || mcp.name
  const deepLink = `ghapp://mcp/install?${encodeURIComponent(
    JSON.stringify({name: canonicalName, type: 'http', url: mcp.url}),
  )}`
  const params = new URLSearchParams({open: deepLink, entry_point: entryPoint})
  return `https://github.com/copilot/app/launch?${params.toString()}`
}

type McpInstallButtonProps = {
  mcp: McpInstallTarget
  copilotAppEntryPoint: string
  muted?: boolean
  ariaLabel?: string
  analyticsReferrerPath?: string
  analyticsResourceName?: string
}

export function McpInstallButton({
  mcp,
  copilotAppEntryPoint,
  muted,
  ariaLabel,
  analyticsReferrerPath,
  analyticsResourceName = mcp.name,
}: McpInstallButtonProps) {
  const isMcpRegistryDevEnabled = isFeatureEnabled('mcp_registry_dev')
  const {sendAnalyticsEvent} = useAnalytics()
  const vscodeUrl = getRegistryServerUrl('vscode', mcp)
  const vscodeInsidersUrl = getRegistryServerUrl('vscode-insiders', mcp)
  const codeOssUrl = getRegistryServerUrl('code-oss', mcp)
  const copilotAppUrl = getCopilotAppInstallUrl(mcp, copilotAppEntryPoint)
  const visualStudioUrl = mcp.url
    ? `vsweb+mcp:/install?${encodeURIComponent(
        JSON.stringify({name: mcp.full_name || mcp.name, type: 'http', url: mcp.url}),
      )}`
    : undefined

  return (
    <ActionMenu>
      <ActionMenu.Button
        variant={muted ? 'default' : 'primary'}
        block={!muted}
        size={muted ? 'small' : 'large'}
        aria-label={ariaLabel}
        onClick={() =>
          sendAnalyticsEvent('analytics.click', '', {
            referrer: analyticsReferrerPath
              ? `${window.location.origin}${analyticsReferrerPath}`
              : window.location.href,
            text: `${analyticsResourceName} install`,
          })
        }
      >
        {muted ? 'Install' : 'Install MCP server'}
      </ActionMenu.Button>

      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Item onSelect={() => window.open(vscodeUrl, '_self')}>
            <ActionList.LeadingVisual>
              <CodeIcon size={16} />
            </ActionList.LeadingVisual>
            Install in VS Code
          </ActionList.Item>

          <ActionList.Item onSelect={() => window.open(vscodeInsidersUrl, '_self')}>
            <ActionList.LeadingVisual>
              <CodeIcon size={16} />
            </ActionList.LeadingVisual>
            Install in VS Code Insiders
          </ActionList.Item>

          {copilotAppUrl && (
            <ActionList.Item onSelect={() => window.open(copilotAppUrl, '_self')}>
              <ActionList.LeadingVisual>
                <CopilotIcon size={16} />
              </ActionList.LeadingVisual>
              Install in GitHub Copilot app
            </ActionList.Item>
          )}

          {isMcpRegistryDevEnabled && codeOssUrl && (
            <ActionList.Item onSelect={() => window.open(codeOssUrl, '_self')}>
              <ActionList.LeadingVisual>
                <CodeIcon size={16} />
              </ActionList.LeadingVisual>
              Install in VS Code development environment
            </ActionList.Item>
          )}

          {isMcpRegistryDevEnabled && visualStudioUrl && (
            <ActionList.Item onSelect={() => window.open(visualStudioUrl, '_self')}>
              <ActionList.LeadingVisual>
                <CodeIcon size={16} />
              </ActionList.LeadingVisual>
              Install in Visual Studio
            </ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
